import { createDoubleRoundRobinSchedule, createLeagueGroups, findClubGroup, simulateScore, type RivalTeam } from './competition';
import { playerById } from './data';
import { resolveDevelopedPlayer } from './development';
import { playerRating } from './competition';
import { SeededRng } from './simulation/rng';
import { AFFINITY_GAMES_COMPETITION_ID, AFFINITY_USER_TEAM_ID, KINGS_CUP_COMPETITION_ID, affinityFixtures, affinityResultFor, affinityTeams, isAffinityUserFixture, neutralWinner } from './specialCompetitions';
import type { AffinityGamesResult, ChampionsResult, CompetitionResult, KingsCupResult, SaveGame } from './types';

export type CareerCompetition = 'league' | 'champions' | 'kings_cup' | 'affinity_games';
export type CalendarFixture = {
  id: string;
  competition: CareerCompetition;
  label: string;
  date: string;
  homeId: string;
  awayId: string;
  home?: RivalTeam;
  away?: RivalTeam;
  matchday?: number;
  round?: ChampionsResult['round'];
  affinityPhase?: 'GROUP' | 'FINAL';
  neutral?: boolean;
  result?: ChampionsResult | KingsCupResult | AffinityGamesResult | { homeScore: number; awayScore: number };
};

const DAY = 86_400_000;
const iso = (date: Date) => date.toISOString().slice(0, 10);
const addDays = (value: string, days: number) => iso(new Date(new Date(`${value}T12:00:00Z`).getTime() + days * DAY));

/** Season one starts in September 2026. League rounds live primarily at the
 * weekend, with a few Friday/Sunday slots. Champions remains a midweek event. */
export function seasonStart(season: number) { return `${2025 + season}-09-01`; }
const LEAGUE_OFFSETS = [4, 11, 18, 24, 32, 38, 46, 53, 59, 67, 74, 80, 88, 95, 101, 109, 116, 123];
export function leagueDate(season: number, matchday: number) {
  const offset = LEAGUE_OFFSETS[matchday - 1] ?? LEAGUE_OFFSETS.at(-1)! + (matchday - LEAGUE_OFFSETS.length) * 7;
  return addDays(seasonStart(season), offset);
}
export function championsDate(season: number, round: ChampionsResult['round']) {
  const offsets: Record<ChampionsResult['round'], number> = { R32: 43, R16: 78, QF: 113, SF: 141, FINAL: 169 };
  return addDays(seasonStart(season), offsets[round]);
}

export function teamMapForSave(save: SaveGame) {
  const groups = createLeagueGroups(save.leagueSeed, save.clubName);
  return new Map(groups.flatMap((group) => group.teams)
    .map((team) => [team.id, developedCalendarTeam(save, team)]));
}

/** Calendar fixtures resolve from the persistent player records, rather than
 * the immutable catalogue ratings. A simulated improvement therefore matters
 * both on the player card and in every later simulated league/Champions tie. */
function developedCalendarTeam(save: SaveGame, team: RivalTeam): RivalTeam {
  const playerIds = team.id === 'team_aurora' ? save.lineup : team.players.map((player) => player.id);
  const records = team.id === 'team_aurora' ? save.clubDevelopment : save.worldDevelopment;
  const roster = playerIds
    .map((id) => playerById.get(id))
    .filter(Boolean)
    .map((player) => resolveDevelopedPlayer(player!, records[player!.id]));
  if (!roster.length) return team;
  const strongest = [...roster].sort((left, right) => playerRating(right) - playerRating(left)).slice(0, 11);
  const rating = Math.round(strongest.reduce((total, player) => total + playerRating(player), 0) / strongest.length);
  return { ...team, players: roster, rating };
}

export function championsFixtures(save: SaveGame): CalendarFixture[] {
  if (!save.championsDraw || save.championsDraw.season !== save.season) return [];
  const teams = teamMapForSave(save);
  const rounds: { key: ChampionsResult['round']; ids: Array<[string, string]> }[] = [];
  let current = save.championsDraw.pairings.map((pairing) => [pairing.firstTeamId, pairing.secondTeamId] as [string, string]);
  rounds.push({ key: 'R32', ids: current });
  for (const key of ['R16', 'QF', 'SF', 'FINAL'] as const) {
    const previous = rounds.at(-1)!;
    const winners = previous.ids.map((_, index) => save.championsResults.find((result) => result.round === previous.key && result.fixtureId === `champions_${previous.key}_${index + 1}`)?.winnerId);
    // A bracket round starts only once every tie in its predecessor has a
    // recorded winner. Partial arrays used to create orphaned next-round
    // fixtures and made a draw look like nobody had advanced.
    if (winners.some((winner) => !winner)) break;
    current = Array.from({ length: Math.floor(winners.length / 2) }, (_, index) => [winners[index * 2], winners[index * 2 + 1]] as [string | undefined, string | undefined])
      .filter((pair): pair is [string, string] => Boolean(pair[0] && pair[1]));
    if (!current.length) break;
    rounds.push({ key, ids: current });
  }
  return rounds.flatMap(({ key, ids }) => ids.map(([homeId, awayId], index) => {
    const fixtureId = `champions_${key}_${index + 1}`;
    const result = save.championsResults.find((entry) => entry.fixtureId === fixtureId);
    return { id: fixtureId, competition: 'champions' as const, label: `Champions · ${championsRoundLabel(key)}`, date: championsDate(save.season, key), homeId, awayId, home: teams.get(homeId), away: teams.get(awayId), round: key, result };
  }));
}

export function kingsCupFixture(save: SaveGame): CalendarFixture | null {
  const draw = save.kingsCupDraw;
  if (!draw || draw.season !== save.season) return null;
  const teams = teamMapForSave(save);
  const home = teams.get(draw.firstTeamId);
  const away = teams.get(draw.secondTeamId);
  if (!home || !away) return null;
  return {
    id: 'kings_cup_final', competition: 'kings_cup', label: "King's Cup · Final neutral", date: draw.date,
    homeId: draw.firstTeamId, awayId: draw.secondTeamId, home, away, neutral: true, result: save.kingsCupResult ?? undefined,
  };
}

export function affinityGamesFixtures(save: SaveGame): CalendarFixture[] {
  if (!save.affinityGames || save.affinityGames.season !== save.season) return [];
  const teams = new Map(affinityTeams(save).map((team) => [team.id, team]));
  return affinityFixtures(save).map((fixture) => ({
    id: fixture.id,
    competition: 'affinity_games' as const,
    label: `Affinity Games · ${fixture.phase === 'FINAL' ? 'Final' : `Clasificación · Partido ${fixture.round}`}`,
    date: fixture.date,
    homeId: fixture.homeId,
    awayId: fixture.awayId,
    home: teams.get(fixture.homeId),
    away: teams.get(fixture.awayId),
    affinityPhase: fixture.phase,
    neutral: fixture.phase === 'FINAL',
    result: affinityResultFor(save, fixture.id),
  }));
}

export function clubCalendarFixtures(save: SaveGame): CalendarFixture[] {
  const groups = createLeagueGroups(save.leagueSeed, save.clubName);
  const group = findClubGroup(groups);
  const teams = new Map(group.teams.map((team) => [team.id, team]));
  const league = createDoubleRoundRobinSchedule(group.teams.map((team) => team.id)).flatMap((day) => day.fixtures
    .filter((fixture) => fixture.homeId === 'team_aurora' || fixture.awayId === 'team_aurora')
    .map((fixture) => ({ id: `league_${day.number}`, competition: 'league' as const, label: `${group.name} · Jornada ${day.number}`, date: leagueDate(save.season, day.number), homeId: fixture.homeId, awayId: fixture.awayId, home: teams.get(fixture.homeId), away: teams.get(fixture.awayId), matchday: day.number, result: save.leagueResults.find((result) => result.competitionId === group.id && result.matchday === day.number) })));
  // The club calendar is intentionally personal. Champions has a global
  // bracket, but this view must never be flooded with other teams' ties.
  const champions = championsFixtures(save).filter((fixture) => fixture.homeId === 'team_aurora' || fixture.awayId === 'team_aurora');
  const kingsCup = [kingsCupFixture(save)].filter((fixture): fixture is CalendarFixture => Boolean(fixture))
    .filter((fixture) => fixture.homeId === 'team_aurora' || fixture.awayId === 'team_aurora');
  // Before the manager confirms the 22-player list there is no side that can
  // actually take the field. The event remains visible in Competiciones and
  // its call-up blocks time, but Calendar only gains playable matches once
  // that temporary team exists.
  const affinity = save.affinityGames?.squadPlayerIds.length
    ? affinityGamesFixtures(save).filter((fixture) => fixture.homeId === AFFINITY_USER_TEAM_ID || fixture.awayId === AFFINITY_USER_TEAM_ID)
    : [];
  return [...league, ...champions, ...kingsCup, ...affinity].sort((a, b) => a.date.localeCompare(b.date));
}

export function nextClubFixture(save: SaveGame) {
  const playable = clubCalendarFixtures(save).filter((fixture) => fixture.competition !== 'affinity_games' || Boolean(save.affinityGames?.squadPlayerIds.length));
  return playable.find((fixture) => !fixture.result && fixture.date >= save.careerDate)
    ?? playable.find((fixture) => !fixture.result)
    ?? null;
}

export function championsRoundLabel(round: ChampionsResult['round']) {
  return ({ R32: 'Dieciseisavos', R16: 'Octavos', QF: 'Cuartos', SF: 'Semifinal', FINAL: 'Final' } as const)[round];
}

export function nextCalendarDay(value: string) { return addDays(value, 1); }

export function resolveChampionsTie(homeId: string, awayId: string, homeScore: number, awayScore: number, seed: number): Pick<ChampionsResult, 'winnerId' | 'penaltyHomeScore' | 'penaltyAwayScore'> {
  if (homeScore !== awayScore) return { winnerId: homeScore > awayScore ? homeId : awayId };
  // Penalties are intentionally independent from the 90-minute score. The
  // same match seed always yields the same shootout, including sudden death.
  const rng = new SeededRng(seed ^ 0x5a17c0de);
  let homePenalties = 0;
  let awayPenalties = 0;
  for (let kick = 0; kick < 5; kick++) {
    homePenalties += rng.next() < .72 ? 1 : 0;
    awayPenalties += rng.next() < .72 ? 1 : 0;
  }
  while (homePenalties === awayPenalties) {
    homePenalties += rng.next() < .72 ? 1 : 0;
    awayPenalties += rng.next() < .72 ? 1 : 0;
  }
  return {
    winnerId: homePenalties > awayPenalties ? homeId : awayId,
    penaltyHomeScore: homePenalties,
    penaltyAwayScore: awayPenalties,
  };
}

/** Resolves non-user ties on a calendar day. User fixtures deliberately stay
 * pending: fast-forward pauses there so the manager never loses a match. */
export function simulateChampionsOnDate(save: SaveGame, date: string): ChampionsResult[] {
  const teams = teamMapForSave(save);
  return championsFixtures(save).filter((fixture) => fixture.date === date && !fixture.result)
    .flatMap((fixture, index) => {
      const home = teams.get(fixture.homeId); const away = teams.get(fixture.awayId);
      if (!home || !away || !fixture.round) return [];
      const [homeScore, awayScore] = simulateScore(home, away, save.nextSeed + index * 811);
      const resolution = resolveChampionsTie(fixture.homeId, fixture.awayId, homeScore, awayScore, save.nextSeed + index * 811);
      return [{ competitionId: 'champions', matchday: 0, fixtureId: fixture.id, round: fixture.round, homeId: fixture.homeId, awayId: fixture.awayId, homeScore, awayScore, ...resolution }];
    });
}

/** A single neutral final: King’s Cup never has a home advantage and uses a
 * shootout when the 90 minutes do not decide the trophy. */
export function simulateKingsCupOnDate(save: SaveGame, date: string): KingsCupResult[] {
  const fixture = kingsCupFixture(save);
  if (!fixture || fixture.date !== date || fixture.result) return [];
  const home = fixture.home; const away = fixture.away;
  if (!home || !away) return [];
  const [homeScore, awayScore] = simulateScore(home, away, save.nextSeed ^ 0x6b1c);
  return [{ competitionId: KINGS_CUP_COMPETITION_ID, matchday: 1, fixtureId: fixture.id, homeId: fixture.homeId, awayId: fixture.awayId, homeScore, awayScore, ...neutralWinner(fixture.homeId, fixture.awayId, homeScore, awayScore, save.nextSeed ^ 0x6b1c) }];
}

/** Background matches are resolved normally. The user's temporary national
 * side is intentionally skipped until its 22-player call-up is confirmed. */
export function simulateAffinityGamesOnDate(save: SaveGame, date: string): AffinityGamesResult[] {
  if (!save.affinityGames?.squadPlayerIds.length) return [];
  const teams = new Map(affinityTeams(save).map((team) => [team.id, team]));
  return affinityGamesFixtures(save).filter((fixture) => fixture.date === date && !fixture.result)
    .flatMap((fixture, index) => {
      const home = teams.get(fixture.homeId); const away = teams.get(fixture.awayId);
      if (!home || !away) return [];
      const [homeScore, awayScore] = simulateScore(home, away, save.nextSeed + index * 617);
      const resolution = fixture.affinityPhase === 'FINAL'
        ? neutralWinner(fixture.homeId, fixture.awayId, homeScore, awayScore, save.nextSeed + index * 617)
        : {};
      return [{ competitionId: AFFINITY_GAMES_COMPETITION_ID, matchday: fixture.affinityPhase === 'FINAL' ? 4 : fixture.id.split('_')[2] ? Number(fixture.id.split('_')[2]) : 0, fixtureId: fixture.id, phase: fixture.affinityPhase ?? 'GROUP', homeId: fixture.homeId, awayId: fixture.awayId, homeScore, awayScore, ...resolution }];
    });
}

/** Every group plays on the same fixture day. This resolver is used only by
 * the career fast-forward; manually opened matches still use the live engine. */
export function simulateLeagueOnDate(save: SaveGame, date: string): CompetitionResult[] {
  const groups = createLeagueGroups(save.leagueSeed, save.clubName);
  const developedTeams = teamMapForSave(save);
  const clubMatchday = createDoubleRoundRobinSchedule(findClubGroup(groups).teams.map((team) => team.id)).find((day) => leagueDate(save.season, day.number) === date);
  if (!clubMatchday) return [];
  const existing = new Set(save.leagueResults.map((result) => `${result.competitionId}:${result.matchday}:${result.homeId}:${result.awayId}`));
  return groups.flatMap((group, groupIndex) => {
    // Every group has its own round-robin rotation. Groups can contain a
    // different number of teams, so only the round number is shared; reusing
    // the club group's fixture IDs here would address teams that do not exist
    // in this group and crash the date simulation.
    const matchday = createDoubleRoundRobinSchedule(group.teams.map((team) => team.id))
      .find((day) => day.number === clubMatchday.number);
    if (!matchday) return [];
    return matchday.fixtures.flatMap((fixture, fixtureIndex) => {
      const key = `${group.id}:${matchday.number}:${fixture.homeId}:${fixture.awayId}`;
      if (existing.has(key)) return [];
      const home = developedTeams.get(fixture.homeId);
      const away = developedTeams.get(fixture.awayId);
      // A malformed or legacy fixture should be skipped rather than making
      // the whole career save unusable. Normal schedules always have both.
      if (!home || !away) return [];
      const [homeScore, awayScore] = simulateScore(home, away, save.nextSeed + groupIndex * 1009 + fixtureIndex * 37);
      return [{ competitionId: group.id, matchday: matchday.number, ...fixture, homeScore, awayScore }];
    });
  });
}
